import React, { useState } from 'react';
import { Download, Upload, Loader2, Send, Copy, Check, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// --- Imports ---
import { useAuth } from '../../contexts/AuthContext';
import { askImageStream } from '../../services/chatApi'; // Ensure this is imported

// Helper to convert file to Base64
const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                // Create a canvas to resize
                const canvas = document.createElement('canvas');
                const MAX_WIDTH = 1024; // Max width in pixels
                
                let width = img.width;
                let height = img.height;

                // Calculate new dimensions
                if (width > MAX_WIDTH) {
                    height *= MAX_WIDTH / width;
                    width = MAX_WIDTH;
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                
                // Export as JPEG with 0.8 quality (greatly reduces size)
                resolve(canvas.toDataURL('image/jpeg', 0.8)); 
            };
            img.onerror = (err) => reject(err);
        };
        reader.onerror = (error) => reject(error);
    });
};

// --- Sub-Components for Access Control ---
const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};


// --- Main Component ---
const AskImage = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();

    // State
    const [inputImage, setInputImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [query, setQuery] = useState("");
    const [resultText, setResultText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasCopied, setHasCopied] = useState(false);

    // Handle File Upload
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setInputImage(file);
            setPreviewUrl(URL.createObjectURL(file));
            setResultText(""); // Reset previous result
            setError(null);
        }
    };

    // Handle Submit
    const handleSubmit = async () => {
        if (!inputImage) {
            setError(t('askImage.errors.noImage'));
            return;
        }

        setIsLoading(true);
        setError(null);
        setResultText(""); // Clear previous result

        try {
            // 1. Prepare Payload
            const fullBase64 = await fileToBase64(inputImage);
            const [meta, rawBase64] = fullBase64.split(',');
            const mimeType = meta.match(/:(.*?);/)[1];
            
            const finalQuery = query.trim() === "" ? t('askImage.queryPlaceholder') : query;

            const payload = {
                prompt: finalQuery,
                image_data: rawBase64,
                mime_type: mimeType
            };

            // 2. Start Request
            const response = await askImageStream(payload);
            
            // 3. Setup Stream Reader
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let done = false;

            // 4. Read Loop
            while (!done) {
                const { value, done: doneReading } = await reader.read();
                done = doneReading;
                
                if (value) {
                    // Decode the chunk
                    const chunkStr = decoder.decode(value, { stream: true });
                    
                    // The server sends lines separated by \n. We need to split them.
                    const lines = chunkStr.split('\n').filter(line => line.trim() !== '');
                    
                    for (const line of lines) {
                        try {
                            const data = JSON.parse(line);
                            
                            // A. Handle Text Content
                            if (data.type === 'content') {
                                setResultText((prev) => prev + data.text);
                            } 
                            // B. Handle Credit Update (End of stream)
                            else if (data.type === 'credits') {
                                if (data.new_credits !== undefined) {
                                    setCredits(data.new_credits);
                                }
                            }
                            // C. Handle Errors
                            else if (data.type === 'error') {
                                setError(data.text);
                            }
                        } catch (e) {
                            console.error("Error parsing JSON chunk", e);
                        }
                    }
                }
            }

        } catch (err) {
            console.error(err);
            setError(err.message || t('askImage.errors.generic'));
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Download as .txt
    const handleDownload = () => {
        if (!resultText) return;
        const element = document.createElement("a");
        const file = new Blob([resultText], {type: 'text/plain'});
        element.href = URL.createObjectURL(file);
        element.download = "extracted_text.txt";
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const handleCopy = () => {
        if (resultText) {
            navigator.clipboard.writeText(resultText);
            setHasCopied(true);
            setTimeout(() => setHasCopied(false), 2000);
        }
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {/* Access Control */}
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : (
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <h3 className="text-3xl font-bold text-orange-500">
                        {t('askImage.title')}
                    </h3>
                    <p className="mt-2 text-gray-800">
                        {t('askImage.description')}
                    </p>
                    <span className="inline-block mt-2 text-xs font-medium text-gray-500 italic">
                        {t('askImage.poweredBy')}
                    </span>

                    {/* Main Grid */}
                    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        
                        {/* LEFT COLUMN: Inputs */}
                        <div className="space-y-6">
                            
                            {/* Upload Section */}
                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-800">
                                    {t('askImage.uploadLabel')}
                                </label>
                                <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-600 rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors relative overflow-hidden">
                                    {previewUrl ? (
                                        <img 
                                            src={previewUrl} 
                                            alt="Preview" 
                                            className="h-full w-full object-contain p-2" 
                                        />
                                    ) : (
                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                            <Upload className="w-10 h-10 mb-3 text-gray-500" />
                                            <p className="text-sm text-gray-600 font-medium">{t('askImage.dropzoneText')}</p>
                                            <p className="text-xs text-gray-500 mt-1">{t('askImage.dropzoneHint')}</p>
                                        </div>
                                    )}
                                    <input 
                                        type="file" 
                                        className="hidden" 
                                        onChange={handleImageUpload} 
                                        accept="image/*,application/pdf" // Added PDF if your backend supports it
                                        disabled={isLoading} 
                                    />
                                </label>
                            </div>

                            {/* Query Input */}
                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-800">
                                    {t('askImage.queryLabel')}
                                </label>
                                <textarea 
                                    className="w-full p-3 bg-white text-gray-800 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:outline-none min-h-[100px] resize-y rounded-md"
                                    placeholder={t('askImage.queryPlaceholder')}
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm">
                                    {error}
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                onClick={handleSubmit}
                                disabled={isLoading || !inputImage}
                                className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-md transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="animate-spin mr-2" size={20} />
                                        {t('askImage.buttons.processing')}
                                    </>
                                ) : (
                                    <>
                                        <Send className="mr-2" size={20} />
                                        {t('askImage.buttons.submit')}
                                    </>
                                )}
                            </button>
                        </div>

                        {/* RIGHT COLUMN: Results */}
                        <div className="flex flex-col h-full">
                            <label className="block text-sm font-medium mb-2 text-gray-800 flex justify-between items-center">
                                {t('askImage.resultLabel')}
                                {resultText && (
                                    <span className="text-xs text-green-600 font-bold">{t('askImage.success')}</span>
                                )}
                            </label>
                            
                            <div className="relative flex-grow relative">
                                <button
                                    onClick={handleCopy}
                                    className="absolute top-2 right-2 p-1.5 bg-slate-200 hover:bg-brand-orange rounded-md"
                                    title={t('translateTool.copyButton')}
                                >
                                    {hasCopied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                                </button>
                                <textarea 
                                    className="w-full h-full min-h-[400px] p-4 bg-slate-50 text-gray-800 border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:outline-none rounded-md resize-none font-mono text-sm"
                                    value={resultText}
                                    onChange={(e) => setResultText(e.target.value)}
                                    placeholder={t('askImage.queryPlaceholderResult')}
                                />
                                {/* Download Button (Floating or fixed at bottom) */}
                                {resultText && (
                                    <button
                                        onClick={handleDownload}
                                        className="absolute bottom-4 right-4 px-4 py-2 bg-white border border-gray-300 shadow-md hover:bg-gray-50 text-gray-800 text-sm font-medium rounded-md flex items-center transition-colors"
                                    >
                                        <Download size={16} className="mr-2 text-orange-500" />
                                        {t('askImage.buttons.download')}
                                    </button>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default AskImage;
